import React from "react";
import ReactHtmlParser, { convertNodeToElement } from "react-html-parser";
import { graphql, useStaticQuery } from "gatsby";
import optimizeImgixUrl from "@herb/utils/optimizeImgixUrl";
import "./index.css";

const normalizeHostname = hostname =>
  // Treat the root domain and its www variant as the same brand domain.
  String(hostname || "")
    .toLowerCase()
    .replace(/^www\./, "")
    .replace(/\.$/, "");

const getBrandHostnames = brands =>
  new Set(
    (brands || [])
      .map(brand => {
        try {
          const url = new URL(brand?.contentBrands?.url || brand?.url);

          return url.protocol === "http:" || url.protocol === "https:"
            ? normalizeHostname(url.hostname)
            : "";
        } catch (error) {
          return "";
        }
      })
      .filter(Boolean)
  );
// These domains are exceptions to the sponsored-link rule.
const sponsoredExceptionHostnames = new Set(["herb.co", "frostyhempco.com"]);
const internalHostnames = new Set(["herb.co"]);

const parseHttpUrl = href => {
  if (!href) {
    return null;
  }

  try {
    const url = new URL(href.startsWith("//") ? `https:${href}` : href);

    return url.protocol === "http:" || url.protocol === "https:" ? url : null;
  } catch (error) {
    return null;
  }
};

const matchesHostnameSet = (hostname, hostnames) => {
  const hostnameParts = normalizeHostname(hostname).split(".");

  return hostnameParts.some((part, index) =>
    hostnames.has(hostnameParts.slice(index).join("."))
  );
};

const isSponsoredHref = (href, brandHostnames) => {
  if (!href || !brandHostnames.size) {
    return false;
  }

  const url = parseHttpUrl(href);

  if (!url || matchesHostnameSet(url.hostname, sponsoredExceptionHostnames)) {
    return false;
  }

  // Check the hostname and each parent domain instead of scanning every brand.
  return matchesHostnameSet(url.hostname, brandHostnames);
};

const isExternalHref = href => {
  const url = parseHttpUrl(href);

  return Boolean(url && !matchesHostnameSet(url.hostname, internalHostnames));
};

const addRelTokens = (rel, tokensToAdd) => {
  const tokens = String(rel || "")
    .split(/\s+/)
    .filter(Boolean);

  // Preserve publisher-provided rel values while avoiding duplicate tokens.
  tokensToAdd.forEach(tokenToAdd => {
    if (!tokens.some(token => token.toLowerCase() === tokenToAdd)) {
      tokens.push(tokenToAdd);
    }
  });

  return tokens.join(" ");
};

const addSponsoredRel = rel => addRelTokens(rel, ["sponsored"]);

const getTagChildren = (node, tagName) =>
  (node?.children || []).filter(
    child => child.type === "tag" && child.name === tagName
  );

const getTableRows = tableNode => {
  const rows = [];

  (tableNode?.children || []).forEach(child => {
    if (child.type === "tag" && child.name === "tr") {
      rows.push(child);
      return;
    }

    if (
      child.type === "tag" &&
      (child.name === "thead" || child.name === "tbody")
    ) {
      rows.push(...getTagChildren(child, "tr"));
    }
  });

  return rows;
};

const getRowCells = rowNode =>
  (rowNode?.children || []).filter(
    child =>
      child.type === "tag" && (child.name === "th" || child.name === "td")
  );

const renderNodeChildren = (node, transform) =>
  (node?.children || []).map((child, index) =>
    convertNodeToElement(child, index, transform)
  );

const ArticleHtmlTable = ({ tableNode, tableIndex, transform }) => {
  const rows = getTableRows(tableNode);

  if (!rows.length) {
    return null;
  }

  const explicitHeaderRow = rows.find(row =>
    getRowCells(row).some(cell => cell.name === "th")
  );
  const headerRow = explicitHeaderRow || rows[0];
  const headerCells = getRowCells(headerRow);
  const bodyRows = rows.filter(row => row !== headerRow);

  const mobileRows = bodyRows.length ? bodyRows : rows.slice(1);

  return (
    <div
      className="descriptionForArticle__tableWrapper"
      key={`article-table-${tableIndex}`}
    >
      <table className="descriptionForArticle__table">
        <thead className="descriptionForArticle__thead">
          <tr className="descriptionForArticle__headerRow">
            {headerCells.map((cell, cellIndex) => (
              <th
                key={`article-table-header-${tableIndex}-${cellIndex}`}
                className={`descriptionForArticle__header ${
                  cellIndex === 0 ? "descriptionForArticle__header--first" : ""
                }`}
              >
                {renderNodeChildren(cell, transform)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="descriptionForArticle__tbody">
          {bodyRows.map((row, rowIndex) => {
            const cells = getRowCells(row);

            return (
              <tr
                key={`article-table-row-${tableIndex}-${rowIndex}`}
                className="descriptionForArticle__row"
              >
                {cells.map((cell, cellIndex) => (
                  <td
                    key={`article-table-cell-${tableIndex}-${rowIndex}-${cellIndex}`}
                    className={`descriptionForArticle__cell ${
                      cellIndex === 0
                        ? "descriptionForArticle__cell--first"
                        : ""
                    }`}
                  >
                    {renderNodeChildren(cell, transform)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="descriptionForArticle__mobileTables">
        {mobileRows.map((row, rowIndex) => {
          const cells = getRowCells(row);
          const titleCell = cells[0];
          const titleContent = titleCell
            ? renderNodeChildren(titleCell, transform)
            : `Row ${rowIndex + 1}`;

          return (
            <div
              key={`article-table-mobile-${tableIndex}-${rowIndex}`}
              className="descriptionForArticle__card"
            >
              <div className="descriptionForArticle__cardHeader">
                {titleContent}
              </div>
              <div className="descriptionForArticle__cardContent">
                {cells.slice(1).map((cell, cellIndex) => {
                  const headerLabel = headerCells[cellIndex + 1]
                    ? renderNodeChildren(headerCells[cellIndex + 1], transform)
                    : null;

                  return (
                    <div
                      key={`article-table-card-${tableIndex}-${rowIndex}-${cellIndex}`}
                      className="descriptionForArticle__cardItem"
                    >
                      {headerLabel && (
                        <div className="descriptionForArticle__cardItemColumnHeader">
                          {headerLabel}
                        </div>
                      )}
                      <div className="descriptionForArticle__cardItemValue">
                        {renderNodeChildren(cell, transform)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DescriptionForArticlePage = ({ item, showSticker }) => {
  const { allWordpressWpBrand: { nodes: brands = [] } = {} } = useStaticQuery(
    graphql`
      query DescriptionForArticleBrandLinks {
        allWordpressWpBrand {
          nodes {
            contentBrands {
              url
            }
          }
        }
      }
    `
  );
  const brandHostnames = React.useMemo(() => getBrandHostnames(brands), [
    brands
  ]);
  const removeDataSaferedirecturl = / data-saferedirecturl="[^"]*"/g;
  let updatedContent = item.text?.replace(removeDataSaferedirecturl, "");

  const imageHTML =
    item?.addSticker && showSticker
      ? `<div class="textStickerContainer">
    <img src="${optimizeImgixUrl(
      item?.sticker420?.sourceUrl
    )}" alt="420 Scavenger Hunt Sticker" style="max-width: 100%; height: auto;">
  </div>`
      : "";

  if (imageHTML) {
    let words = updatedContent.split(/\s+/);

    let middleIndex = Math.floor(words.length / 2);

    words.splice(middleIndex, 0, imageHTML);

    updatedContent = words.join(" ");
  }

  const transform = (node, index) => {
    if (node.type === "tag" && node.name === "table") {
      // Table cells are rendered manually, so pass the anchor transform through.
      return (
        <ArticleHtmlTable
          key={`description-table-${index}`}
          tableNode={node}
          tableIndex={index}
          transform={transform}
        />
      );
    }

    if (node.type === "tag" && node.name === "a" && node.attribs?.href) {
      const { href } = node.attribs;
      let rel = node.attribs.rel;

      if (isExternalHref(href)) {
        rel = addRelTokens(rel, ["noopener", "noreferrer"]);
      }

      if (isSponsoredHref(href, brandHostnames)) {
        rel = addSponsoredRel(rel);
      }

      if (rel) {
        node.attribs.rel = rel;
      }

      return convertNodeToElement(node, index, transform);
    }

    return undefined;
  };

  return (
    <div className="descriptionForArticle">
      <div className="descriptionTextP">
        {ReactHtmlParser(updatedContent, { transform })}
      </div>
    </div>
  );
};

export default DescriptionForArticlePage;
